import React from 'react'
import { ResponsiveContainer, Cell } from 'recharts'
import { Card, CardContent, CardHeader } from '../ui'
import { useI18n } from '../../i18n'
import { componentScope } from '../../lib/cssScope'

interface SupplierRiskData {
  supplierId: string
  supplierName: string
  riskScore: number | null
}

interface SupplierRiskHeatmapProps {
  data: SupplierRiskData[]
}

const SupplierRiskHeatmap: React.FC<SupplierRiskHeatmapProps> = ({ data }) => {
  const { t } = useI18n()

  const getRiskColor = (risk: number | null): string => {
    if (risk === null || risk === undefined) return '#e5e7eb' // gray for no data
    if (risk >= 80) return '#ef4444' // red - critical
    if (risk >= 60) return '#f59e0b' // orange - high
    if (risk >= 40) return '#eab308' // yellow - medium
    if (risk >= 20) return '#3b82f6' // blue - low
    return '#10b981' // green - very low
  }

  const getRiskLabel = (risk: number | null): string => {
    if (risk === null || risk === undefined) return t('tpsm.charts.noData', 'No Data')
    if (risk >= 80) return t('tpsm.charts.critical', 'Critical')
    if (risk >= 60) return t('tpsm.charts.high', 'High')
    if (risk >= 40) return t('tpsm.charts.medium', 'Medium')
    if (risk >= 20) return t('tpsm.charts.low', 'Low')
    return t('tpsm.charts.veryLow', 'Very Low')
  }

  return (
    <Card className={`${componentScope('SupplierRiskHeatmap')} metric-card`}>
      <CardHeader>
        <h2 className="text-base font-semibold text-[var(--ds-text,#111827)] mb-2">
          {t('tpsm.charts.riskHeatmap.title', 'Supplier Risk Heatmap')}
        </h2>
        <div className="text-sm text-gray-500">
          {t('tpsm.charts.riskHeatmap.subtitle', 'Risk scores across all suppliers')}
        </div>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-500">
            <div className="text-center">
              <div className="text-sm">{t('common.noData', 'No data available')}</div>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {data.map((item) => (
              <div key={item.supplierId} className="flex items-center gap-3">
                <div className="w-32 text-sm text-gray-700 truncate" title={item.supplierName}>
                  {item.supplierName}
                </div>
                <div className="flex-1 flex items-center gap-2">
                  <div
                    className="h-8 rounded flex-1 transition-all hover:opacity-80 cursor-pointer"
                    style={{
                      backgroundColor: getRiskColor(item.riskScore),
                      minWidth: '100px'
                    }}
                    title={`${item.supplierName}: ${item.riskScore !== null ? item.riskScore : 'N/A'}`}
                  />
                  <div className="w-20 text-xs text-gray-600 text-right">
                    {item.riskScore !== null ? `${item.riskScore}` : 'N/A'}
                  </div>
                  <div className="w-24 text-xs text-gray-500">
                    {getRiskLabel(item.riskScore)}
                  </div>
                </div>
              </div>
            ))}
            {/* Legend */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-xs font-medium text-gray-700 mb-2">
                {t('tpsm.charts.legend', 'Legend')}:
              </div>
              <div className="flex flex-wrap gap-3 text-xs">
                {[
                  { label: t('tpsm.charts.critical', 'Critical'), color: '#ef4444', min: 80 },
                  { label: t('tpsm.charts.high', 'High'), color: '#f59e0b', min: 60 },
                  { label: t('tpsm.charts.medium', 'Medium'), color: '#eab308', min: 40 },
                  { label: t('tpsm.charts.low', 'Low'), color: '#3b82f6', min: 20 },
                  { label: t('tpsm.charts.veryLow', 'Very Low'), color: '#10b981', min: 0 }
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-1">
                    <div
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-gray-600">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default SupplierRiskHeatmap


